import { useBreakpointDown } from '@all-manager/design-system-v2';
import { useEffect, useMemo } from 'react';
import { isDestinationVisible, NAVIGATION_GROUPS } from '../../../../components/Layout/navigationGroups';
import { usePermissions } from '../../../../hooks/usePermissions';
import { useSpace } from '../../../../hooks/useSpace';
import useCompleteOnboarding from '../../api/useCompleteOnboarding';
import { useOnboardingEligibility } from '../../hooks/useOnboardingEligibility';
import {
  autoOpenOnboarding,
  closeOnboarding,
  setOnboardingStep,
  useIsOnboardingOpen,
  useOnboardingActiveStep,
} from '../../store/onboardingStore';
import { getOnboardingSteps } from '../../utils/getOnboardingSteps';

// The shell switches to its phone top bar and drawer below `md`.
const PHONE_BREAKPOINT = 'md';

export function useOnboardingDialog() {
  const { currentSpace } = useSpace();
  const { consultationRole, isDue } = useOnboardingEligibility();
  const permissions = usePermissions();
  const isOpen = useIsOnboardingOpen();
  const activeStep = useOnboardingActiveStep();
  const isPhone = useBreakpointDown(PHONE_BREAKPOINT);
  const completeOnboarding = useCompleteOnboarding();
  const isVisible = consultationRole !== null;

  // First visit: open once per session, the completion is saved when the member leaves the dialog.
  useEffect(() => {
    if (isDue) {
      autoOpenOnboarding();
    }
  }, [isDue]);

  // Moved to a space they manage while it was open: close it so the store does not hold other dialogs back.
  useEffect(() => {
    if (isOpen && !isVisible) {
      closeOnboarding();
    }
  }, [isOpen, isVisible]);

  const steps = useMemo(() => {
    if (!consultationRole) {
      return [];
    }
    return getOnboardingSteps({ spaceName: currentSpace?.name ?? '', role: consultationRole, isPhone });
  }, [consultationRole, currentSpace?.name, isPhone]);

  const highlightedIds = steps[activeStep]?.highlightedIds ?? [];

  // The menu as this member sees it; on phones, only the group being presented: the whole menu would push the text below the fold.
  const previewGroups = NAVIGATION_GROUPS
    .map((group) => ({
      title: group.title,
      destinations: group.destinations.filter((destination) => isDestinationVisible(destination, permissions)),
    }))
    .filter((group) => !isPhone || highlightedIds.length === 0
      || group.destinations.some((destination) => highlightedIds.includes(destination.id)));

  function handleLeave() {
    closeOnboarding();
    if (isDue) {
      completeOnboarding.mutate();
    }
  }

  return {
    isVisible,
    isOpen,
    steps,
    activeStep,
    highlightedIds,
    previewGroups,
    previewCaption: isPhone ? 'Ces rubriques sont dans le menu, en haut à gauche.' : 'Ces rubriques sont dans le menu, à gauche.',
    handleStepChange: setOnboardingStep,
    handleSkip: handleLeave,
    handleFinish: handleLeave,
  };
}
